import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { ReactComponent as LeftArrowIcon } from '../../icons/arrow.left.svg';
import { ReactComponent as RightArrowIcon } from '../../icons/arrow.right.svg';
import { ReactComponent as CloseIcon } from '../../icons/xmark.svg';

// Props 인터페이스 정의
interface FullScreenImageSliderProps {
  isOpen: boolean;
  imageUrls: string[]; // 이미지 URL 배열
  onClosed: () => void;
  focusRef?: React.RefObject<HTMLElement>; // 닫을 때 포커스를 돌려줄 요소
  onPrevImage?: (index: number) => void;
  onNextImage?: (index: number) => void;
}

const FullScreenImageSlider: React.FC<FullScreenImageSliderProps> = ({
  isOpen,
  imageUrls,
  onClosed,
  focusRef,
  onPrevImage,
  onNextImage
}) => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isPrev, setIsPrev] = useState<boolean>(false);
  const [hasNavigated, setHasNavigated] = useState<boolean>(false);
  const [pendingAction, setPendingAction] = useState<'prev' | 'next' | 'goto' | null>(null);
  const [pendingIndex, setPendingIndex] = useState<number | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    const originalViewportContent = viewport?.getAttribute('content') || '';

    document.body.style.overflow = 'hidden';
    viewport?.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=3.0');

    return () => {
      viewport?.setAttribute('content', originalViewportContent);
      document.body.style.overflow = 'auto';
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      // Reset to initial state when the slider opens
      setHasNavigated(false);
      setPendingAction(null);
      setPendingIndex(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (pendingAction && pendingIndex !== null) {
      setCurrentIndex(pendingIndex);

      // Call the appropriate callback
      if (pendingAction === 'prev' && onPrevImage) {
        onPrevImage(pendingIndex);
      } else if (pendingAction === 'next' && onNextImage) {
        onNextImage(pendingIndex);
      } else if (pendingAction === 'goto') {
        if (pendingIndex > currentIndex && onNextImage) {
          onNextImage(pendingIndex);
        } else if (pendingIndex < currentIndex && onPrevImage) {
          onPrevImage(pendingIndex);
        }
      }

      // Reset pending state
      setPendingAction(null);
      setPendingIndex(null);
    }
  }, [pendingAction, pendingIndex, currentIndex, onNextImage, onPrevImage]);

  // ESC 키를 누르면 모달 닫기
  useEffect((): (() => void) => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        handleClose();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden'; // 스크롤 방지
    }

    return (): void => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto'; // 스크롤 복원
    };
  }, [isOpen]);

  // 모달 외부 클릭 시 닫기
  useEffect((): (() => void) => {
    const handleOutsideClick = (e: MouseEvent): void => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return (): void => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);

  // 모달이 닫힐 때 focusRef로 포커스 이동
  useEffect((): (() => void) => {
    return (): void => {
      if (!isOpen && focusRef && focusRef.current) {
        focusRef.current.focus();
      }
    };
  }, [isOpen, focusRef]);

  //크게보기 이미지 크기 감지
  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      if (entries.length > 0) {
        const entry = entries[0];
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          setImageSize({ width, height });
        }
      }
    });

    if (imageRef.current) {
      observer.observe(imageRef.current);
    }

    return () => {
      if (imageRef.current) {
        observer.unobserve(imageRef.current);
      }
    };
  }, []);

  const handleClose = (): void => {
    if (onClosed) {
      onClosed();
    }
  };

  const handlePrev = (): void => {
    // Set animation properties first
    setIsPrev(true);
    setHasNavigated(true);

    // Calculate new index
    const newIndex = currentIndex > 0 ? currentIndex - 1 : imageUrls.length - 1;

    // Set pending action and index
    setPendingAction('prev');
    setPendingIndex(newIndex);
  };

  const handleNext = (): void => {
    // Set animation properties first
    setIsPrev(false);
    setHasNavigated(true);

    // Calculate new index
    const newIndex = currentIndex < imageUrls.length - 1 ? currentIndex + 1 : 0;

    // Set pending action and index
    setPendingAction('next');
    setPendingIndex(newIndex);
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999999] flex items-center justify-center bg-black bg-opacity-75"
    >
      <motion.div
        ref={modalRef}
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="relative w-full h-full flex items-center justify-center"
      >
        {/* 이미지 컨테이너 */}
        <div className="w-full h-full flex items-center justify-center overflow-hidden">
          {imageUrls && imageUrls.length > 0 && (
            <AnimatePresence mode="wait">
              <motion.img
                key={currentIndex}
                src={imageUrls[currentIndex]}
                alt={`AttachedImage ${currentIndex + 1}`}
                initial={{
                  opacity: 0,
                  x: hasNavigated ? (isPrev ? -100 : 100) : 0
                }}
                animate={{
                  opacity: 1,
                  x: 0
                }}
                exit={{
                  opacity: 0,
                  x: hasNavigated ? (isPrev ? 100 : -100) : 0
                }}
                transition={{
                  duration: 0.3,
                  ease: 'easeInOut'
                }}
                className="max-h-full max-w-full object-contain"
                ref={imageRef} // 이미지 크기 참조
              />
            </AnimatePresence>
          )}
          {/* 닫기 버튼 */}
          <div
            className="flex items-center justify-center absolute w-[34px] h-[34px] z-10 rounded-full bg-black bg-opacity-50 hover:bg-opacity-75"
            style={{
              top: `calc(50% - ${imageSize.height / 2}px + 8px)`,
              left: `calc(50% + ${imageSize.width / 2}px - 42px)`
            }}
            onClick={handleClose}
          >
            <CloseIcon className="fill-white" width={'16px'} height={'16px'} />
          </div>
        </div>

        {/* 이미지가 여러 장인 경우에만 네비게이션 버튼 표시 */}
        {imageUrls && imageUrls.length > 1 && (
          <>
            {/* 이전 버튼 */}
            <button
              type="button"
              onClick={handlePrev}
              className="flex items-center justify-center absolute w-[34px] h-[34px] left-[24px] top-1/2 transform -translate-y-1/2 rounded-full bg-black bg-opacity-50 hover:bg-opacity-75"
              aria-label="Previous image"
            >
              <LeftArrowIcon className="fill-white" width={'16px'} height={'16px'} />
            </button>

            {/* 다음 버튼 */}
            <button
              type="button"
              onClick={handleNext}
              className="flex items-center justify-center absolute w-[34px] h-[34px] right-[24px] top-1/2 transform -translate-y-1/2 rounded-full bg-black bg-opacity-50 hover:bg-opacity-75"
              aria-label="Next image"
            >
              <RightArrowIcon className="fill-white" width={'16px'} height={'16px'} />
            </button>

            {/* 이미지 인디케이터 */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
              {imageUrls.map((imageUrl, index: number) => (
                <button
                  type="button"
                  key={`Indicator-${imageUrl.replace(/[^a-zA-Z0-9]/g, '')}`}
                  className={`w-3 h-3 rounded-full ${
                    index === currentIndex ? 'bg-white' : 'bg-gray-400'
                  }`}
                  onClick={(): void => {
                    // Don't update if clicking the current index
                    if (index === currentIndex) return;

                    // Set animation properties first
                    setIsPrev(index < currentIndex);
                    setHasNavigated(true);

                    // Set pending action and index
                    setPendingAction('goto');
                    setPendingIndex(index);
                  }}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

export default FullScreenImageSlider;
